import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { environment } from '../../../../environments/environment';
import { KeycloakService } from '../../../services/keycloak/keycloak.service';
import { UserInfo } from 'src/app/models/user-info.model';
import { UsersService } from 'src/app/services/users/users.service';
import { Subject, filter, takeUntil } from 'rxjs';
import { EntNavigationService } from 'src/app/services/navigation/navigation.service';
import { EntNavigation } from 'src/app/navigation/ent-navigation';
import { Router, RouterLinkActive, RouterLink } from '@angular/router';
import { RouteStatusService } from 'src/app/services/maintenance/route-status.service';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { UserRightsHelper } from 'src/app/models/role.model';
import { NgClass, NgStyle } from '@angular/common';
import {
  DefaultClassDirective,
  DefaultStyleDirective,
} from '@ngbracket/ngx-layout/extended';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';

export interface SideNavToggle {
  screenWidth: number;
  collapsed: boolean;
}

@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    NgClass,
    DefaultClassDirective,
    MatIcon,
    NgStyle,
    DefaultStyleDirective,
    RouterLinkActive,
    RouterLink,
    MatButton,
    TranslatePipe,
  ],
})
export class SidenavComponent implements OnInit, OnDestroy, OnChanges {
  @Input({ required: true }) currentUser: UserInfo | null;
  @Input() selectedFileUrl: string | null = null;
  @Output() toggleSidenavEvent = new EventEmitter<SideNavToggle>();
  @Output() toggleSidebarForMe = new EventEmitter<void>();

  navigation: EntNavigation[] = [];

  //Design of menu
  screenWidth = 0;
  collapsed = true;
  isToggled = false;
  isSmallScreen = false;
  isStudent = false;
  canOpen = true;
  navSupport: { link: string; icon: string; label: string }[];

  isPasswordChangeDisabled = true;
  isTicketingDisabled = false;
  hasAccessAdministration = false;
  isAvsAlign = false;

  private onDestroy$ = new Subject();

  constructor(
    private _userService: UsersService,
    private _keycloakService: KeycloakService,
    private _entNavigationService: EntNavigationService,
    private _router: Router,
    private _routeStatusService: RouteStatusService,
    private _translateService: TranslateService
  ) {
    this.verifySize();
    this.updateNavSupport();

    // Set the private defaults
    if (window.innerWidth > 1280) this.toggleCollapse();
  }

  ngOnInit() {
    // Watch for user firstname and lastname change events
    this._userService.userChanged
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.currentUser.firstName = this._userService.userFirstname;
        this.currentUser.lastName = this._userService.userLastname;
      });

    // Watch for navigation change events
    this._entNavigationService.onNavigationChanged
      .pipe(
        filter(value => value !== null),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => {
        this.navigation = this._entNavigationService.getCurrentNavigation();
      });

    // Watch for maintenance mode settings
    const urls = ['/self', '/ticket'];
    this._routeStatusService
      .getRouteStatusForUrls(urls)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(statuses => {
        this.isPasswordChangeDisabled = statuses['/self'];
        this.isTicketingDisabled = statuses['/ticket'];
      });

    // Watch for language change events
    this._translateService.onLangChange
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.updateNavSupport();
      });

    this.isAvsAlign = this._userService.checkIsAvsAlign(this.currentUser);
  }

  /**
   * Update the content of the property navSupport.
   */
  private updateNavSupport() {
    this.navSupport = [
      {
        link: this._entNavigationService.getSupportLink(
          this._translateService.currentLang
        ),
        icon: 'live_help',
        label: 'SIDENAV.SUPPORT_TEACHER',
      },
      {
        link: environment.redirectionServiceNow,
        icon: 'help',
        label: 'SIDENAV.OPEN-TICKET',
      },
    ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentUser'] && changes['currentUser'].currentValue) {
      this.setMenuBasedOnAccessRights();
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(true);
    this.onDestroy$.unsubscribe();
  }

  //When size changed, use this function
  @HostListener('window:resize', ['$event'])
  onResize() {
    this.verifySize();
  }

  //Verify size of screen
  verifySize() {
    if (!this.isSmallScreen && window.innerWidth <= 1280) {
      this.openToggleCollapse();
      this.isSmallScreen = true;
    } else if (this.isSmallScreen && window.innerWidth > 1280) {
      this.screenWidth = window.innerWidth;
      this.toggleSidenavEvent.emit({
        collapsed: this.collapsed,
        screenWidth: this.screenWidth,
      });
      this.isSmallScreen = false;
    }
  }

  //Toggle menu when small screen
  toggleSidenav() {
    this.toggleSidebarForMe.emit(null);
  }

  //Open sidenav
  openSidenav() {
    if (!this.isToggled && this.canOpen) {
      this.collapsed = true;
      this.toggleSidenavEvent.emit({
        collapsed: this.collapsed,
        screenWidth: this.screenWidth,
      });
    }

    this.canOpen = true;
  }

  //Close sidenav
  closeSidenav() {
    if (!this.isToggled) {
      this.collapsed = false;
      this.toggleSidenavEvent.emit({
        collapsed: this.collapsed,
        screenWidth: this.screenWidth,
      });
    }
  }

  //Keep sidenav open or close it
  toggleCollapse() {
    this.isToggled = !this.isToggled;

    if (this.isToggled) {
      this.screenWidth = window.innerWidth;
      this.toggleSidenavEvent.emit({
        collapsed: this.collapsed,
        screenWidth: this.screenWidth,
      });
    } else {
      this.screenWidth = 0;
      this.closeSidenav();
    }
  }

  //Open sidenav and toggle it
  openToggleCollapse() {
    this.collapsed = true;
    this.isToggled = true;
    this.screenWidth = 0;
    this.toggleSidenavEvent.emit({
      collapsed: this.collapsed,
      screenWidth: this.screenWidth,
    });
  }

  get misalignedEmail(): string {
    const first = this.currentUser?.misalignedFirstname ?? '';
    const last = this.currentUser?.misalignedName ?? '';
    return `${first}.${last}@edu.vs.ch`.toLowerCase();
  }

  //Logout button
  logout(): void {
    this._userService.clearStorageAndLogout();
    this._keycloakService.logout(environment.logoutURL);
    this._keycloakService.clearToken();
  }

  private setMenuBasedOnAccessRights() {
    //Different access levels
    let isTeacher = false;
    let isRefInfo = false;
    let isNewRefInfo = false;
    let hasAccessMenu4 = false;
    let isUserManager = false;
    let isGestionnaireEDUID = false;
    let isITPasswordManagerS2P = false;
    this.isStudent = false;

    // Check if user has access to Administration
    this.hasAccessAdministration = this.currentUser.services.some(service =>
      service.subtype.includes('admin')
    );

    const roleChecker = new UserRightsHelper(
      this.currentUser.roles,
      this.currentUser.orgs
    );

    if (roleChecker.isSupportICT()) {
      this._entNavigationService.setCurrentNavigation(
        'MenuSupport',
        this.hasAccessAdministration
      );
      return;
    }

    // Set Navigation based on User Role
    if (roleChecker.isITManagerExtended()) {
      isNewRefInfo = true;
    }
    if (roleChecker.isDirector() || roleChecker.isSecretary()) {
      hasAccessMenu4 = true;
      isUserManager = true;
    }
    if (
      roleChecker.isGestionnaire() ||
      roleChecker.isInspecteur() ||
      roleChecker.isConseillerPedagogique() ||
      roleChecker.isAdminSE() ||
      roleChecker.isConseilDirection()
    ) {
      hasAccessMenu4 = true;
    }
    if (roleChecker.isRefInfo()) {
      isRefInfo = true;
    }
    if (roleChecker.isGestionnaireEDUID()) {
      isGestionnaireEDUID = true;
    }
    if (roleChecker.isITPasswordManagerS2P()) {
      isITPasswordManagerS2P = true;
    }
    if (roleChecker.isTeacher()) {
      isTeacher = true;
    }
    if (roleChecker.isStudent()) {
      this.isStudent = true;
    }

    /* Load menu from the most to the least privileged role:
    - SupportICTVS
    - New Ref Info
    - Menu 6
    - Menu 4
    - Ref Info
    - Main
    - Student
    */
    if (isNewRefInfo) {
      this._entNavigationService.setCurrentNavigation(
        'newrefinfo',
        this.hasAccessAdministration
      );
    } else if (hasAccessMenu4) {
      if (isRefInfo) {
        this._entNavigationService.setCurrentNavigation(
          'newrefinfo',
          this.hasAccessAdministration
        );
      } else if (isUserManager || isTeacher || isGestionnaireEDUID) {
        this._entNavigationService.setCurrentNavigation(
          'menu6',
          this.hasAccessAdministration
        );
      } else {
        this._entNavigationService.setCurrentNavigation(
          'menu4',
          this.hasAccessAdministration
        );
      }
    } else if (isRefInfo) {
      this._entNavigationService.setCurrentNavigation(
        'refinfo',
        this.hasAccessAdministration
      );
    } else if (isTeacher || isGestionnaireEDUID || isITPasswordManagerS2P) {
      this._entNavigationService.setCurrentNavigation(
        'main',
        this.hasAccessAdministration
      );
    } else {
      this._entNavigationService.setCurrentNavigation(
        'student',
        this.hasAccessAdministration
      );
    }
  }

  navigateSelf() {
    this._router.navigate(['/self']);
  }
}
